import RandExp from "randexp"
import { POSTCODE_REGEXES } from "../../../helpers/countryCodes"
import { postJson, setFieldError } from "./request"

const MAX_DIMENSION = 274
const MAX_WEIGHT = 700

let comboCount = 0

// Turns a native <select> into a searchable combobox with flags. The select
// stays in the DOM (hidden) and keeps the chosen value.
function initCountryCombo(select, onChange) {
  const listId = `hv-combo-list-${++comboCount}`
  const options = Array.from(select.options)
    .filter((option) => option.value)
    .map((option) => ({
      value: option.value,
      name: option.textContent.trim(),
      code: option.dataset.code,
      flag: option.dataset.flag,
      search: [option.textContent, ...(option.dataset.keywords || "").split("|")].join("|").toLowerCase(),
    }))
  const placeholder = select.querySelector('option[value=""]')

  const wrapper = document.createElement("div")
  wrapper.className = "hv-combo"
  wrapper.innerHTML = `
    <img class="hv-combo__flag" alt="" hidden>
    <input class="hv-input hv-combo__input" type="text" role="combobox" autocomplete="off" spellcheck="false"
      aria-autocomplete="list" aria-expanded="false" aria-controls="${listId}">
    <svg class="hv-combo__caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
    <ul class="hv-combo__list" id="${listId}" role="listbox" hidden></ul>`
  select.insertAdjacentElement("afterend", wrapper)
  select.hidden = true
  select.tabIndex = -1

  const input = wrapper.querySelector("input")
  const flag = wrapper.querySelector(".hv-combo__flag")
  const list = wrapper.querySelector("ul")
  input.id = select.id
  select.id = `${select.id}-native`
  input.setAttribute("aria-labelledby", select.getAttribute("aria-labelledby"))
  input.placeholder = placeholder ? placeholder.textContent : ""

  let visible = []
  let active = -1

  const selected = () => options.find((option) => option.value === select.value)

  const showSelected = () => {
    const option = selected()
    input.value = option ? option.name : ""
    flag.hidden = !(option && option.flag)
    if (option && option.flag) flag.src = option.flag
  }

  const render = () => {
    list.innerHTML = ""
    visible.forEach((option, index) => {
      const li = document.createElement("li")
      li.id = `${listId}-${index}`
      li.setAttribute("role", "option")
      li.setAttribute("aria-selected", String(option.value === select.value))
      li.className = index === active ? "is-active" : ""
      li.innerHTML = option.flag ? `<img alt="" loading="lazy" src="${option.flag}"><span></span>` : `<i class="hv-combo__noflag"></i><span></span>`
      li.querySelector("span").textContent = option.name
      li.addEventListener("mousedown", (event) => event.preventDefault())
      li.addEventListener("click", () => choose(option))
      list.appendChild(li)
    })
    if (visible.length === 0) {
      const li = document.createElement("li")
      li.className = "hv-combo__empty"
      li.textContent = "Šalis nerasta"
      list.appendChild(li)
    }
    if (active >= 0) {
      input.setAttribute("aria-activedescendant", `${listId}-${active}`)
      const activeItem = list.children[active]
      if (activeItem) activeItem.scrollIntoView({ block: "nearest" })
    } else {
      input.removeAttribute("aria-activedescendant")
    }
  }

  const open = (query) => {
    const q = (query || "").trim().toLowerCase()
    visible = q ? options.filter((option) => option.search.includes(q)) : options
    active = Math.max(0, visible.findIndex((option) => option.value === select.value))
    if (visible.length === 0) active = -1
    list.hidden = false
    input.setAttribute("aria-expanded", "true")
    render()
  }

  const close = () => {
    list.hidden = true
    input.setAttribute("aria-expanded", "false")
    input.removeAttribute("aria-activedescendant")
    showSelected()
  }

  const choose = (option) => {
    select.value = option.value
    close()
    onChange(option)
  }

  // Focus alone (Tab, or the submit button jumping to an invalid field) must not
  // pop the list open; a click, typing or ArrowDown does.
  input.addEventListener("focus", () => input.select())
  input.addEventListener("click", () => {
    if (list.hidden) open("")
  })
  input.addEventListener("input", () => open(input.value))
  input.addEventListener("blur", close)
  input.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (list.hidden) return open("")
      if (visible.length === 0) return
      const step = event.key === "ArrowDown" ? 1 : -1
      active = (active + step + visible.length) % visible.length
      render()
    } else if (event.key === "Enter") {
      if (!list.hidden && visible[active]) {
        event.preventDefault()
        choose(visible[active])
      }
    } else if (event.key === "Escape" && !list.hidden) {
      event.stopPropagation()
      close()
    }
  })

  showSelected()
  return { input, selected }
}

export default function initCalculator(form) {
  const examples = {}
  const sides = {}

  const zipError = (side) => {
    const { zip, combo } = sides[side]
    const value = zip.value.trim()
    const country = combo.selected()
    const regex = country && POSTCODE_REGEXES[country.code]
    if (!value || !regex || regex.test(value)) return ""
    if (!examples[country.code]) examples[country.code] = new RandExp(regex).gen()
    return `Pvz.: ${examples[country.code]}`
  }

  const dimensionError = (input) => {
    const raw = input.value.trim()
    if (input.dataset.dim === "weight") {
      if (!raw) return "Privaloma"
      const weight = Number(raw.replace(/,/g, "."))
      if (Number.isNaN(weight)) return "Privalo būti skaičiai"
      if (weight > MAX_WEIGHT) return `max ${MAX_WEIGHT}`
      return ""
    }
    if (!raw) return "Privaloma"
    if (/^\d+[.,]\d*$/.test(raw)) return "Įveskite cm be kablelio"
    if (!/^\d+$/.test(raw)) return "Įveskite skaičių"
    if (Number(raw) > MAX_DIMENSION) return `max ${MAX_DIMENSION}`
    return ""
  }

  const dims = Array.from(form.querySelectorAll("[data-dim]"))

  const validators = []
  const addValidator = (input, check) => {
    const run = () => {
      const message = check()
      setFieldError(input, message)
      return !message
    }
    input.addEventListener("blur", () => {
      if (input.value || input.getAttribute("aria-invalid")) run()
    })
    validators.push({ input, run })
  }

  ;["sender", "receiver"].forEach((side) => {
    const select = form.querySelector(`[data-country-for="${side}"]`)
    const zip = form.querySelector(`[data-zip-for="${side}"]`)
    const nameField = form.querySelector(`[data-name-for="${side}"]`)
    const combo = initCountryCombo(select, (option) => {
      nameField.value = option.name
      setFieldError(combo.input, "")
      if (zip.value) setFieldError(zip, zipError(side))
    })
    const current = combo.selected()
    if (current) nameField.value = current.name
    sides[side] = { select, zip, combo }

    addValidator(combo.input, () => (select.value ? "" : "Privaloma"))
    addValidator(zip, () => zipError(side))
  })
  dims.forEach((input) => addValidator(input, () => dimensionError(input)))

  const submit = form.querySelector(".hv-calc__submit")
  const submitText = submit.querySelector(".hv-calc__submit-text")
  const formError = form.querySelector(".hv-calc__formerror")

  form.addEventListener("submit", (event) => {
    event.preventDefault()
    formError.hidden = true
    const invalid = validators.filter(({ run }) => !run())
    if (invalid.length) {
      invalid[0].input.focus()
      return
    }

    const value = (name) => form.querySelector(`[name="${name}"]`).value.trim()
    const dim = (key) => form.querySelector(`[data-dim="${key}"]`).value.trim()
    const body = {
      order: {
        ignore_build_items: true,
        google_autocomplete: true,
        google_sender: value("order[google_sender]"),
        google_receiver: value("order[google_receiver]"),
        sender_country_id: Number(sides.sender.select.value),
        sender_postal_code: sides.sender.zip.value.trim(),
        receiver_country_id: Number(sides.receiver.select.value),
        receiver_postal_code: sides.receiver.zip.value.trim(),
        items_attributes: [
          { x: dim("x"), y: dim("y"), z: dim("z"), weight: dim("weight").replace(/,/g, "."), amount: 1 },
        ],
        commit: "Skaičiuoti kainą",
      },
    }

    submit.disabled = true
    submit.setAttribute("aria-busy", "true")
    submitText.textContent = "Skaičiuojama…"

    postJson("/price", body)
      .then(() => {
        window.location.href = "/order_steps"
      })
      .catch(() => {
        formError.textContent = "Nepavyko apskaičiuoti kainos. Patikrinkite duomenis ir bandykite dar kartą."
        formError.hidden = false
        submit.disabled = false
        submit.removeAttribute("aria-busy")
        submitText.textContent = "Skaičiuoti kainą"
      })
  })

  // "Skaičiuoti kainą" links further down the page jump back to the calculator.
  document.querySelectorAll("[data-focus-calc]").forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault()
      form.scrollIntoView({ behavior: "smooth", block: "center" })
      sides.receiver.combo.input.focus({ preventScroll: true })
    })
  })
}
